// src/hooks/useScreenerSignal.js
// SCREENER-FRAG — new file. Logic extracted from ScreenerPage.jsx (previously
// inline refs + scheduleLog). Content unchanged; only the boundary changed —
// this hook owns every D3 Tier 3 logging concern and nothing else.
//
// dataAsOf/marketStatus are NOT constructor arguments — they come from
// useScreenerData, which itself needs this hook's scheduleLog to exist
// first (passed into useScreenerData's onFetchComplete). Threading them in
// as constructor args would be circular. Instead this hook exposes
// setDataAsOf/setMarketStatus setters (same pattern as setAlertsCount) that
// the shell calls in a useEffect once useScreenerData's state is known.
//
// PIECE 3 (this session) extends the original payload with per-filter-change
// granularity — see recordFieldFilterChange — so the signal moves from
// "which scan was clicked" to "which field/operator/preset was touched,"
// deepening the L2 strategy_preference signal per the handover's Tier 3
// signal chain.

import { useRef, useEffect, useCallback } from 'react';
import { API_URL } from '../constants/screenerConfig';

export function useScreenerSignal({ isLoggedIn, visibleCols }) {
  const sessionId        = useRef('scr-' + Date.now().toString(36) + '-' + performance.now().toString(36).replace('.', ''));
  const sessionStart      = useRef(Date.now());
  const lastFilters       = useRef({});
  const logTimerRef       = useRef(null);
  const tickersClicked    = useRef([]);
  const scanSequence      = useRef([]);
  const dataAsOfRef       = useRef(null);
  const marketStatusRef   = useRef(null);
  const savedScanUsedRef  = useRef(null);
  const alertsCountRef    = useRef(0);
  // PIECE 3 — new: field-level filter interactions this session, e.g.
  // { key: 'sector', category: 'descriptive', value: 'Technology', ts }
  const fieldChangesRef   = useRef([]);

  useEffect(() => {
    return () => { if (logTimerRef.current) clearTimeout(logTimerRef.current); };
  }, []);

  // ── Debounced D3 log — fires 2s after last filter change ─────
  const scheduleLog = useCallback((filters, resultCount, tickers) => {
    if (logTimerRef.current) clearTimeout(logTimerRef.current);
    logTimerRef.current = setTimeout(() => {
      const duration = Math.round((Date.now() - sessionStart.current) / 1000);
      fetch(`${API_URL}/api/screener/signal-log`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          sessionId:       sessionId.current,
          isGuest:         !isLoggedIn,
          filters,
          resultCount,
          tickersViewed:   tickers.map(t => t.ticker),
          tickersClicked:  tickersClicked.current,
          sessionDuration: duration,
          scanSequence:    scanSequence.current,
          dataAsOf:        dataAsOfRef.current,
          marketStatus:    marketStatusRef.current,
          savedScanUsed:   savedScanUsedRef.current,
          alertsActive:    alertsCountRef.current,
          columnsSelected: Array.from(visibleCols),
          // PIECE 3
          fieldFilterChanges: fieldChangesRef.current,
        }),
      }).catch(() => {});
    }, 2000);
  }, [isLoggedIn, visibleCols]);

  // ── ALL functions below wrapped in useCallback with empty deps (BUG FIX,
  // this session's live-testing round): every one of these previously was a
  // plain function declaration, meaning useScreenerSignal returned a fresh
  // object with fresh function identities on EVERY render. That's harmless
  // on its own, but it's exactly the kind of thing that turns into a real
  // infinite-render-loop the moment any consumer passes one of these into
  // another hook's dependency array (which is precisely what happened in
  // ScreenerPage.jsx — see that file's fix for the actual bug this caused:
  // 2,400+ requests/sec against /api/screener/results in production).
  // Fixing it here too, not just in the consumer, so the next place that
  // wires these up doesn't hit the same trap.
  const recordTickerClick = useCallback((ticker) => {
    if (!tickersClicked.current.includes(ticker)) {
      tickersClicked.current = [...tickersClicked.current, ticker];
    }
  }, []);

  const recordScanUsed = useCallback((scanKey) => {
    if (scanKey && !scanSequence.current.includes(scanKey)) {
      scanSequence.current = [...scanSequence.current, scanKey];
    }
  }, []);

  const setSavedScanUsed = useCallback((scanId) => {
    savedScanUsedRef.current = scanId;
  }, []);

  const setAlertsCount = useCallback((count) => {
    alertsCountRef.current = count;
  }, []);

  // Called by the shell whenever useScreenerData's dataAsOf/marketStatus
  // change — see file header for why these aren't constructor args.
  const setDataAsOf = useCallback((value) => {
    dataAsOfRef.current = value;
  }, []);

  const setMarketStatus = useCallback((value) => {
    marketStatusRef.current = value;
  }, []);

  // PIECE 3 — field registry interactions (FilterPanel onChange)
  const recordFieldFilterChange = useCallback((key, category, value) => {
    fieldChangesRef.current = [
      ...fieldChangesRef.current,
      { key, category, value, ts: Date.now() },
    ];
  }, []);

  return {
    scheduleLog,
    recordTickerClick,
    recordScanUsed,
    setSavedScanUsed,
    setAlertsCount,
    setDataAsOf,
    setMarketStatus,
    recordFieldFilterChange,
    lastFilters,
  };
}
